import { createRouter, createWebHistory } from 'vue-router'

declare module 'vue-router' {
  interface RouteMeta {
    public?: boolean
    admin?: boolean
    showOrgSidebar?: boolean
  }
}
import Login from '@/views/Login.vue'
import Home from '@/views/Home.vue'
import Dashboard from '@/views/Dashboard.vue'
import AppLink from '@/views/AppLink.vue'
import HelpCenter from '@/views/HelpCenter.vue'
import HelpArticle from '@/views/HelpArticle.vue'
import HelpReachCoverage from '@/views/HelpReachCoverage.vue'
import HelpUniqueCoverage from '@/views/HelpUniqueCoverage.vue'
import AdminHome from '@/views/AdminHome.vue'
import AdminBrands from '@/views/AdminBrands.vue'
import AdminSectors from '@/views/AdminSectors.vue'
import AdminAdvertisers from '@/views/AdminAdvertisers.vue'
import AdminImports from '@/views/AdminImports.vue'
import AdminOrganization from '@/views/AdminOrganization.vue'
import AdminOrganizationContent from '@/views/AdminOrganizationContent.vue'
import AdminOrganizationUsers from '@/views/AdminOrganizationUsers.vue'
import AdminOrganizationSetup from '@/views/AdminOrganizationSetup.vue'
import AdminPreview from '@/views/AdminPreview.vue'
import OrganizationDisabled from '@/views/OrganizationDisabled.vue'
import { useAuthStore } from '@/stores/auth'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/login',
      name: 'login',
      component: Login,
      meta: { public: true },
      props: { mode: 'default' },
    },
    {
      path: '/welcome',
      name: 'welcome',
      component: Login,
      meta: { public: true },
      props: { mode: 'invite' },
    },
    { path: '/', name: 'home', component: Home },
    { path: '/dashboard/:id', name: 'dashboard', component: Dashboard },
    { path: '/apps/:appId', name: 'app-link', component: AppLink },
    { path: '/help', name: 'help', component: HelpCenter },
    { path: '/help/reach-coverage', name: 'help-reach-coverage', component: HelpReachCoverage },
    { path: '/help/unique-coverage', name: 'help-unique-coverage', component: HelpUniqueCoverage },
    { path: '/help/:slug', name: 'help-article', component: HelpArticle },
    { path: '/admin', name: 'admin', component: AdminHome, meta: { admin: true } },
    { path: '/admin/brands', name: 'admin-brands', component: AdminBrands, meta: { admin: true } },
    {
      path: '/admin/sectors',
      name: 'admin-sectors',
      component: AdminSectors,
      meta: { admin: true },
    },
    {
      path: '/admin/advertisers',
      name: 'admin-advertisers',
      component: AdminAdvertisers,
      meta: { admin: true },
    },
    {
      path: '/admin/imports',
      name: 'admin-imports',
      component: AdminImports,
      meta: { admin: true },
    },
    // IMPORTANT: /new must come before /:slug to avoid matching slug='new'
    {
      path: '/admin/organization/new',
      name: 'admin-create-organization',
      component: AdminOrganization,
      meta: { admin: true },
    },
    {
      path: '/admin/organization/:slug',
      name: 'admin-organization',
      component: AdminOrganization,
      meta: { admin: true, showOrgSidebar: true },
    },
    {
      path: '/admin/organization/:slug/content',
      name: 'admin-organization-content',
      component: AdminOrganizationContent,
      meta: { admin: true, showOrgSidebar: true },
    },
    {
      path: '/admin/organization/:slug/users',
      name: 'admin-organization-users',
      component: AdminOrganizationUsers,
      meta: { admin: true, showOrgSidebar: true },
    },
    {
      path: '/admin/organization/:slug/setup',
      name: 'admin-organization-setup',
      component: AdminOrganizationSetup,
      meta: { admin: true, showOrgSidebar: true },
    },
    {
      path: '/admin/preview/:orgId',
      name: 'admin-preview',
      component: AdminPreview,
      meta: { admin: true },
    },
    {
      path: '/admin/preview/:orgId/dashboard/:id',
      name: 'admin-preview-dashboard',
      component: AdminPreview,
      meta: { admin: true },
    },
    {
      path: '/organization-disabled',
      name: 'organization-disabled',
      component: OrganizationDisabled,
      meta: { public: true },
    },
    { path: '/:pathMatch(.*)*', redirect: '/' },
  ],
})

router.beforeEach((to) => {
  const auth = useAuthStore()
  const isPublic = Boolean(to.meta.public)
  const needsAdmin = Boolean(to.meta.admin)

  // redirect unauthenticated users to login
  if (!isPublic && !auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
  // prevent /admin access for non-admin users
  if (needsAdmin && auth.isAuthenticated && !auth.isAdmin) {
    return { name: 'home' }
  }
  // redirect logged-in admin to admin routes (except /help)
  if (
    auth.isAuthenticated &&
    auth.isAdmin &&
    !needsAdmin &&
    !isPublic &&
    !to.path.startsWith('/help')
  ) {
    return { name: 'admin' }
  }
  // redirect to home or admin if already authenticated
  if (to.name === 'login' && auth.isAuthenticated) {
    return { name: auth.isAdmin ? 'admin' : 'home' }
  }
})

export default router
